import React, { useCallback, useEffect, useState } from 'react';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  DownOutlined,
  MinusCircleOutlined,
  StopOutlined,
  SyncOutlined,
  UpOutlined,
} from '@ant-design/icons';
import {
  Badge,
  Modal,
  Pagination,
  Spin,
  Tag,
  Typography,
} from '@duxton-web/ui';
import { styled } from 'styled-components';

import {
  CanaryMetricConfig,
  CanaryPhaseDTO,
  CanaryRunDTO,
  MetricCheckEntry,
} from '../CanaryStatus/types';
import {
  CanaryMonitorStatusPanel,
  getAggregateMonitorStatus,
  getDatadogMonitorNames,
  getDatadogMonitorStatusColor,
  getLatestMonitorStatuses,
  isDatadogMonitorName,
  withoutDatadogMonitors,
} from '@/features/variable/canary-monitor-status';
import Service from '@/lib/App';

const PAGE_SIZE = 10;

const RUN_STATUS_BADGE: Record<
  string,
  'success' | 'processing' | 'error' | 'default' | 'warning'
> = {
  active: 'processing',
  completed: 'success',
  rolled_back: 'error',
  cancelled: 'default',
};

const PHASE_STATUS_COLOR: Record<string, string> = {
  completed: 'green',
  in_progress: 'blue',
  rolled_back: 'red',
  awaiting_decision: 'orange',
  threshold_breached: 'red',
  yet_to_start: 'default',
  skipped: 'purple',
};

const PHASE_STATUS_LABEL: Record<string, string> = {
  completed: 'Completed',
  in_progress: 'In Progress',
  rolled_back: 'Rolled Back',
  awaiting_decision: 'Awaiting Decision',
  threshold_breached: 'Threshold Breached',
  yet_to_start: 'Yet to Start',
  skipped: 'Skipped',
};

const DECISION_COLOR: Record<string, string> = {
  user_promoted: 'green',
  user_rolled_back: 'red',
  user_overridden: 'orange',
  advance: 'blue',
  user_held: 'default',
  promoted: 'green',
  rolled_back: 'red',
  cancelled: 'default',
};

const DECISION_LABEL: Record<string, string> = {
  user_promoted: 'Promoted',
  user_rolled_back: 'Rolled Back',
  user_overridden: 'Overridden',
  advance: 'Advanced',
  user_held: 'Held',
  promoted: 'Promoted',
  rolled_back: 'Rolled Back',
  cancelled: 'Cancelled',
};

// ─── Helpers ───────────────────────────────────────────────────────────────────

const lastCheckLogSnapshot = (
  log?: MetricCheckEntry[],
): Record<string, number> | null => {
  if (!log || log.length === 0) return null;
  for (let i = log.length - 1; i >= 0; i--) {
    if (log[i].metrics && Object.keys(log[i].metrics!).length > 0) {
      return log[i].metrics!;
    }
  }
  return null;
};

// ─── Metric display ────────────────────────────────────────────────────────────

const MetricValues = ({
  log,
  metricsConfig,
}: {
  log: MetricCheckEntry[];
  metricsConfig?: CanaryMetricConfig[];
}) => {
  const last = log[log.length - 1];

  // Show metric failure notice if the most recent entry is an error
  const failedCount = log.filter((e) => !!e.error).length;
  const failureNotice = failedCount > 0 && (
    <Typography.Text
      type="warning"
      style={{ fontSize: 11, display: 'block', marginBottom: 4 }}
    >
      ⚠ {failedCount} check{failedCount > 1 ? 's' : ''} failed (metric read
      error
      {last?.error ? `: ${last.error}` : ''})
    </Typography.Text>
  );

  const lastWithData = [...log]
    .reverse()
    .find(
      (e) =>
        Object.keys(withoutDatadogMonitors(e.metrics, metricsConfig) ?? {})
          .length > 0,
    );

  if (!lastWithData) {
    return (
      <>
        {failureNotice}
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          No metric values recorded
        </Typography.Text>
      </>
    );
  }

  const queryMetrics = withoutDatadogMonitors(
    lastWithData.metrics,
    metricsConfig,
  )!;

  return (
    <>
      {failureNotice}
      <MetricGrid>
        {Object.entries(queryMetrics).map(([name, value]) => {
          const breached = lastWithData.breachedMetrics?.includes(name);
          return (
            <MetricItem key={name} $breached={!!breached}>
              <MetricName>{name}</MetricName>
              <MetricValue $breached={!!breached}>
                {typeof value === 'number' ? value.toFixed(3) : value}
              </MetricValue>
              {breached && (
                <Tag color="red" style={{ fontSize: 10, padding: '0 4px' }}>
                  Breached
                </Tag>
              )}
            </MetricItem>
          );
        })}
      </MetricGrid>
    </>
  );
};

const MetricSnapshot = ({
  start,
  end,
  endLabel = 'End',
  metricsConfig,
}: {
  start?: Record<string, number> | null;
  end?: Record<string, number> | null;
  endLabel?: string;
  metricsConfig?: CanaryMetricConfig[];
}) => {
  if (!start && !end) return null;
  const keys = Array.from(
    new Set([...Object.keys(start ?? {}), ...Object.keys(end ?? {})]),
  ).filter((name) => !isDatadogMonitorName(name, metricsConfig));
  if (!keys.length) return null;

  return (
    <SnapshotTable>
      <thead>
        <tr>
          <SnapshotTh>Metric</SnapshotTh>
          <SnapshotTh>Start</SnapshotTh>
          <SnapshotTh>{endLabel}</SnapshotTh>
          <SnapshotTh>Δ</SnapshotTh>
        </tr>
      </thead>
      <tbody>
        {keys.map((key) => {
          const s = start?.[key];
          const e = end?.[key];
          const cfg = metricsConfig?.find(
            (m) => m.displayName === key || m.query === key,
          );
          const delta = s != null && e != null ? e - s : null;
          const deltaStr =
            delta != null ? `${delta >= 0 ? '+' : ''}${delta.toFixed(3)}` : '—';
          const deltaColor =
            delta == null
              ? undefined
              : delta > 0
                ? '#52c41a'
                : delta < 0
                  ? '#ff4d4f'
                  : undefined;
          return (
            <tr key={key}>
              <SnapshotTd>
                <Typography.Text style={{ fontSize: 11 }}>
                  {cfg?.displayName ?? key}
                </Typography.Text>
              </SnapshotTd>
              <SnapshotTd>{s != null ? s.toFixed(3) : '—'}</SnapshotTd>
              <SnapshotTd>{e != null ? e.toFixed(3) : '—'}</SnapshotTd>
              <SnapshotTd style={{ color: deltaColor, fontWeight: 600 }}>
                {deltaStr}
              </SnapshotTd>
            </tr>
          );
        })}
      </tbody>
    </SnapshotTable>
  );
};

// ─── Phase row ─────────────────────────────────────────────────────────────────

const PhaseRow = ({
  phase,
  metricsConfig,
}: {
  phase: CanaryPhaseDTO;
  metricsConfig?: CanaryMetricConfig[];
}) => {
  const monitorNames = getDatadogMonitorNames(metricsConfig);
  const monitorStatuses = getLatestMonitorStatuses(
    phase.metricCheckLog,
    metricsConfig,
    phase.status,
  );
  const monitorHealth = getAggregateMonitorStatus(
    monitorStatuses.map(({ status }) => status),
  );

  return (
    <PhaseWrapper $breached={phase.status === 'threshold_breached'}>
      <PhaseHeaderRow>
        <PhaseLabel>Phase {phase.phaseIndex + 1}</PhaseLabel>
        <Tag color={PHASE_STATUS_COLOR[phase.status] ?? 'default'}>
          {PHASE_STATUS_LABEL[phase.status] ?? phase.status.replace(/_/g, ' ')}
        </Tag>
        {monitorNames.length > 0 && (
          <Tag color={getDatadogMonitorStatusColor(monitorHealth)}>
            Monitors: {monitorHealth}
          </Tag>
        )}
        <PhaseStats>
          <span>{phase.targetPercentage}% traffic</span>
          <span>·</span>
          <span>{phase.durationMins} min</span>
          <span>·</span>
          <span>{phase.totalChecks} checks</span>
          {phase.breachCount > 0 && (
            <>
              <span>·</span>
              <span style={{ color: '#ff4d4f', fontWeight: 600 }}>
                {phase.breachCount}/{phase.totalChecks} breached
              </span>
            </>
          )}
        </PhaseStats>
        {phase.decision && (
          <Tag color={DECISION_COLOR[phase.decision] ?? 'default'}>
            {DECISION_LABEL[phase.decision] ?? phase.decision}
            {phase.decisionBy ? ` by ${phase.decisionBy}` : ''}
          </Tag>
        )}
      </PhaseHeaderRow>
      {(phase.metricValuesAtStart ||
        phase.metricValuesAtEnd ||
        lastCheckLogSnapshot(phase.metricCheckLog)) && (
        <MetricSnapshot
          start={phase.metricValuesAtStart}
          end={
            phase.metricValuesAtEnd ??
            lastCheckLogSnapshot(phase.metricCheckLog)
          }
          endLabel={phase.metricValuesAtEnd ? 'End' : 'Current'}
          metricsConfig={metricsConfig}
        />
      )}
      <CanaryMonitorStatusPanel
        phaseStatus={phase.status}
        entries={phase.metricCheckLog}
        metricsConfig={metricsConfig}
        compact
      />
      {phase.metricCheckLog && phase.metricCheckLog.length > 0 && (
        <MetricValues
          log={phase.metricCheckLog}
          metricsConfig={metricsConfig}
        />
      )}
      {phase.decisionReason && (
        <DecisionReason>
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
            Reason: {phase.decisionReason}
          </Typography.Text>
        </DecisionReason>
      )}
      {phase.startedAt && (
        <PhaseTime>
          Started: {new Date(phase.startedAt).toLocaleString()}
          {phase.actualEndAt &&
            ` · Ended: ${new Date(phase.actualEndAt).toLocaleString()}`}
        </PhaseTime>
      )}
    </PhaseWrapper>
  );
};

// ─── Expandable run detail ──────────────────────────────────────────────────────

const RunDetail = ({ runId }: { runId: number }) => {
  const [phases, setPhases] = useState<CanaryPhaseDTO[]>([]);
  const [metricsConfig, setMetricsConfig] = useState<
    CanaryMetricConfig[] | undefined
  >(undefined);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Service.planner!.fetchCanaryRun(runId)
      .then((data: any) => {
        if (cancelled) return;
        setPhases(data?.phases || []);
        setMetricsConfig(data?.run?.canaryConfigSnapshot?.metrics);
      })
      .catch(() => {
        if (cancelled) return;
        setPhases([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [runId]);

  if (loading) {
    return (
      <DetailLoading>
        <Spin size="small" />
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Loading phases…
        </Typography.Text>
      </DetailLoading>
    );
  }

  if (!phases.length) {
    return (
      <Typography.Text type="secondary">
        No phase data available.
      </Typography.Text>
    );
  }

  return (
    <PhasesWrapper>
      {phases.map((phase) => (
        <PhaseRow key={phase.id} phase={phase} metricsConfig={metricsConfig} />
      ))}
    </PhasesWrapper>
  );
};

// ─── Run card ───────────────────────────────────────────────────────────────────

const RunStatusIcon = ({ status }: { status: string }) => {
  switch (status) {
    case 'active':
      return (
        <SyncOutlined spin style={{ color: '#1890ff' }} {...({} as any)} />
      );
    case 'completed':
      return (
        <CheckCircleOutlined style={{ color: '#52c41a' }} {...({} as any)} />
      );
    case 'rolled_back':
      return (
        <MinusCircleOutlined style={{ color: '#ff4d4f' }} {...({} as any)} />
      );
    case 'cancelled':
      return <StopOutlined style={{ color: '#8c8c8c' }} {...({} as any)} />;
    default:
      return null;
  }
};

const RunCard = ({
  run,
  expanded,
  onToggle,
}: {
  run: CanaryRunDTO;
  expanded: boolean;
  onToggle: () => void;
}) => (
  <RunCardWrapper $active={run.status === 'active'}>
    <RunCardHeader onClick={onToggle}>
      <RunCardLeft>
        <RunStatusIcon status={run.status} />
        <RunIdText>Run #{run.id}</RunIdText>
        <Badge
          status={RUN_STATUS_BADGE[run.status] ?? 'default'}
          text={run.status.replace(/_/g, ' ')}
        />
      </RunCardLeft>
      <RunCardRight>
        <RunMetaText>by {run.createdBy}</RunMetaText>
        <RunMetaText>
          <ClockCircleOutlined style={{ marginRight: 4 }} {...({} as any)} />
          {new Date(run.startedAt).toLocaleString()}
        </RunMetaText>
        {run.finalDecision && (
          <Tag color={DECISION_COLOR[run.finalDecision] ?? 'default'}>
            Final: {DECISION_LABEL[run.finalDecision] ?? run.finalDecision}
          </Tag>
        )}
        {expanded ? (
          <UpOutlined style={{ fontSize: 12 }} {...({} as any)} />
        ) : (
          <DownOutlined style={{ fontSize: 12 }} {...({} as any)} />
        )}
      </RunCardRight>
    </RunCardHeader>
    {expanded && (
      <RunCardBody onClick={(e) => e.stopPropagation()}>
        <RunDetail runId={run.id} />
      </RunCardBody>
    )}
  </RunCardWrapper>
);

// ─── Main modal ─────────────────────────────────────────────────────────────────

interface CanaryHistoryModalProps {
  variableName: string;
  open: boolean;
  onClose: () => void;
}

const CanaryHistoryModal: React.FC<CanaryHistoryModalProps> = ({
  variableName,
  open,
  onClose,
}) => {
  const [runs, setRuns] = useState<CanaryRunDTO[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [expandedRunId, setExpandedRunId] = useState<number | null>(null);

  const fetchRuns = useCallback(
    async (pageNum: number) => {
      setLoading(true);
      const offset = (pageNum - 1) * PAGE_SIZE;
      const data = await Service.planner!.listCanaryRuns(
        variableName,
        undefined,
        PAGE_SIZE,
        offset,
      );
      setRuns(data?.runs || []);
      setTotal(data?.total || 0);
      setLoading(false);
    },
    [variableName],
  );

  useEffect(() => {
    if (open) {
      setPage(1);
      setExpandedRunId(null);
      fetchRuns(1);
    }
  }, [open, fetchRuns]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    setExpandedRunId(null);
    fetchRuns(newPage);
  };

  const toggleExpand = (runId: number) => {
    setExpandedRunId((prev) => (prev === runId ? null : runId));
  };

  return (
    <Modal
      title={
        <ModalTitle>
          Canary Run History
          {total > 0 && (
            <TotalBadge>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {total} total run{total !== 1 ? 's' : ''}
              </Typography.Text>
            </TotalBadge>
          )}
        </ModalTitle>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width={800}
      styles={{
        body: {
          maxHeight: 'calc(100vh - 220px)',
          overflowY: 'auto',
          padding: '12px 24px',
        },
      }}
    >
      {loading ? (
        <LoadingWrapper>
          <Spin />
        </LoadingWrapper>
      ) : runs.length === 0 ? (
        <EmptyState>
          <Typography.Text type="secondary">
            No canary runs found for <strong>{variableName}</strong>.
          </Typography.Text>
        </EmptyState>
      ) : (
        <>
          <RunList>
            {runs.map((run) => (
              <RunCard
                key={run.id}
                run={run}
                expanded={expandedRunId === run.id}
                onToggle={() => toggleExpand(run.id)}
              />
            ))}
          </RunList>
          {total > PAGE_SIZE && (
            <PaginationWrapper>
              <Pagination
                current={page}
                pageSize={PAGE_SIZE}
                total={total}
                onChange={handlePageChange}
                showSizeChanger={false}
                size="small"
              />
            </PaginationWrapper>
          )}
        </>
      )}
    </Modal>
  );
};

// ─── Styled components ──────────────────────────────────────────────────────────

const ModalTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TotalBadge = styled.span`
  font-weight: 400;
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
`;

const EmptyState = styled.div`
  padding: 32px;
  text-align: center;
`;

const RunList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const RunCardWrapper = styled.div<{ $active?: boolean }>`
  border: 1px solid ${({ $active }) => ($active ? '#1890ff' : '#e8e8e8')};
  border-radius: 8px;
  overflow: hidden;
  background: ${({ $active }) => ($active ? '#f0f7ff' : '#fff')};
`;

const RunCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  cursor: pointer;
  user-select: none;
  &:hover {
    background: rgba(0, 0, 0, 0.02);
  }
`;

const RunCardLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RunCardRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RunIdText = styled(Typography.Text)`
  font-weight: 600;
  font-size: 13px;
`;

const RunMetaText = styled(Typography.Text)`
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
`;

const RunCardBody = styled.div`
  border-top: 1px solid #e8e8e8;
  padding: 12px 14px;
  background: #fafafa;
`;

const PhasesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PhaseWrapper = styled.div<{ $breached?: boolean }>`
  padding: 8px 12px;
  background: ${({ $breached }) => ($breached ? '#fff2f0' : '#fff')};
  border: 1px solid ${({ $breached }) => ($breached ? '#ffccc7' : '#f0f0f0')};
  border-radius: 6px;
`;

const PhaseHeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const PhaseLabel = styled(Typography.Text)`
  font-weight: 600;
  font-size: 12px;
  min-width: 56px;
`;

const PhaseStats = styled.div`
  display: flex;
  gap: 4px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
`;

const PhaseTime = styled.div`
  font-size: 11px;
  color: rgba(0, 0, 0, 0.35);
  margin-top: 4px;
`;

const DetailLoading = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
`;

const MetricGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
`;

const MetricItem = styled.div<{ $breached: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid ${({ $breached }) => ($breached ? '#ffccc7' : '#e8e8e8')};
  background: ${({ $breached }) => ($breached ? '#fff2f0' : '#fafafa')};
  min-width: 80px;
`;

const MetricName = styled.span`
  font-size: 11px;
  color: rgba(0, 0, 0, 0.45);
`;

const MetricValue = styled.span<{ $breached: boolean }>`
  font-size: 13px;
  font-weight: 600;
  color: ${({ $breached }) => ($breached ? '#ff4d4f' : '#262626')};
`;

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 16px;
`;

const DecisionReason = styled.div`
  margin-top: 4px;
  padding: 2px 0;
`;

const SnapshotTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;
  margin-top: 6px;
  background: #fafafa;
  border-radius: 4px;
  overflow: hidden;
`;

const SnapshotTh = styled.th`
  text-align: left;
  padding: 3px 8px;
  color: rgba(0, 0, 0, 0.45);
  font-weight: 500;
  border-bottom: 1px solid #f0f0f0;
`;

const SnapshotTd = styled.td`
  padding: 3px 8px;
  color: rgba(0, 0, 0, 0.65);
  border-bottom: 1px solid #f5f5f5;
`;

export default CanaryHistoryModal;
